import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Target, GitBranch, Building2, Swords, Landmark, CalendarClock, FileText,
} from 'lucide-react'
import './OpportunityDetail.css'
import {
  PageHeader, SectionCard, Badge, EmptyState, Button,
  DescriptionList, type DLRow,
  AssociationCard, AssocRow, QuickActionBar, toActionContacts,
} from '@/components/ui'
import type { BadgeTone } from '@/components/ui'
import { RecordHero } from '@/components/workspace'
import { Timeline } from '@/components/timeline'
import { useStore } from '@/store/useStore'
import {
  accountById, contactById, contactName, contactsForAccount, trackById, fundById, propertyById, latest,
} from '@/lib/selectors'
import { money, date, daysUntil } from '@/lib/format'
import type {
  Opportunity, OpportunityType, CompetitivePosition, SalesType, KeyDateStatus,
} from '@/types/entities'

const TYPE_TONE: Record<OpportunityType, BadgeTone> = {
  JV: 'navy',
  'Equity Partner': 'info',
  '3PM': 'ok',
  Acquisition: 'warn',
  Development: 'warn',
}
const POSITION_TONE: Record<CompetitivePosition, BadgeTone> = {
  'Partner of Choice': 'ok',
  'Competitive Plus': 'ok',
  Competitive: 'neutral',
  'Competitive Minus': 'warn',
}
const SALES_TONE: Record<SalesType, BadgeTone> = {
  Suspect: 'neutral',
  Lead: 'info',
  Prospect: 'info',
  Opportunity: 'navy',
}
const KD_TONE: Record<KeyDateStatus, BadgeTone> = {
  Upcoming: 'neutral',
  'Due Soon': 'warn',
  Overdue: 'alert',
  Met: 'ok',
  Waived: 'neutral',
}

export default function OpportunityDetail() {
  const { id } = useParams()
  const data = useStore((s) => s.data)
  const opp = useMemo(() => data.opportunities.find((o) => o.id === id), [data, id])

  if (!opp) {
    return (
      <>
        <PageHeader eyebrow="Opportunity" title={<>Deal <b>Not Found</b></>} />
        <SectionCard pad>
          <EmptyState
            icon={<Target size={36} />}
            title="Opportunity not found"
            hint={<>No deal matches <code>{id}</code>.</>}
            action={<Link to="/opportunities"><Button variant="primary">Back to Pipeline</Button></Link>}
          />
        </SectionCard>
      </>
    )
  }

  const principal = accountById(data, opp.principalAccountId)
  const sourceContact = contactById(data, opp.sourceContactId)
  const sourceAccount = accountById(data, opp.sourceAccountId)
  const primaryContact = contactById(data, opp.primaryContactId)
  const track = trackById(data, opp.trackId)
  const fund = fundById(data, opp.fundId)
  const won = /won/i.test(opp.salesStage)

  const linkedProps = (opp.propertyIds ?? [])
    .map((pid) => propertyById(data, pid))
    .filter((p): p is NonNullable<typeof p> => !!p)

  const keyDates = (opp.keyDateIds ?? [])
    .map((kid) => data.keyDates.find((k) => k.id === kid))
    .filter((k): k is NonNullable<typeof k> => !!k)

  const activities = data.activities.filter((a) =>
    a.attachedTo.some((r) => r.kind === 'opportunity' && r.id === opp.id),
  )

  /* ---- hero KPIs ---- */
  const heroKpis = [
    { label: 'Deal Size', value: opp.dealSize != null ? money(opp.dealSize, { compact: true }) : '—' },
    { label: 'Sales Type', value: opp.salesType },
    { label: 'Stage', value: opp.salesStage },
    {
      label: opp.type === '3PM' ? 'Properties' : 'Est. Close',
      value: opp.type === '3PM'
        ? (linkedProps.length || '—')
        : (opp.estCloseQuarter ?? (opp.estCloseDate ? date(opp.estCloseDate, 'short') : '—')),
    },
  ]

  /* ---- common key info ---- */
  const commonRows: DLRow[] = [
    {
      label: 'Principal Account',
      value: principal ? <Link to={`/accounts/${principal.id}`}>{principal.name}</Link> : '—',
    },
    {
      label: 'Primary Contact',
      value: primaryContact
        ? <>
            <Link to={`/contacts/${primaryContact.id}`}>{contactName(primaryContact)}</Link>
            {opp.primaryContactRole && <span className="dim"> · {opp.primaryContactRole}</span>}
          </>
        : '—',
    },
    { label: 'Sales Type', value: <Badge tone={SALES_TONE[opp.salesType]} variant="soft">{opp.salesType}</Badge> },
    {
      label: 'Stage',
      value: <Badge tone={won ? 'ok' : 'neutral'} variant="soft" dot={won}>{opp.salesStage}</Badge>,
    },
    { label: 'Deal Size', value: opp.dealSize != null ? money(opp.dealSize) : '—' },
    { label: 'Est. Close', value: opp.estCloseQuarter ?? (opp.estCloseDate ? date(opp.estCloseDate, 'long') : '—') },
    {
      label: 'Relationship Track',
      value: track
        ? <Link to={`/accounts/${track.accountId}/deals`}>{track.name}</Link>
        : '—',
    },
    {
      label: 'Fund / Vehicle',
      value: fund ? <Link to={`/funds/${fund.id}`}>{fund.name}</Link> : '—',
    },
  ]

  return (
    <>
      <RecordHero
        eyebrow="Opportunity"
        title={opp.name}
        breadcrumb={<><Link to="/opportunities">Pipeline</Link> <span className="dim">/</span> {opp.type}</>}
        badges={
          <>
            <Badge tone={TYPE_TONE[opp.type]} variant="solid">{opp.type}</Badge>
            <Badge tone={won ? 'ok' : 'neutral'} variant="soft" dot={won}>{opp.salesStage}</Badge>
            {opp.competitivePosition && (
              <Badge tone={POSITION_TONE[opp.competitivePosition]} variant="soft">{opp.competitivePosition}</Badge>
            )}
          </>
        }
        kpis={heroKpis}
        actions={
          <QuickActionBar
            contacts={toActionContacts(principal ? contactsForAccount(data, principal.id) : [])}
            attachTo={{ kind: 'opportunity', id: opp.id, label: opp.name }}
            contextLabel={opp.name}
          />
        }
      />

      <div className="opp-layout">
        {/* ---------- MAIN ---------- */}
        <div className="opp-main">
          {/* advisor-sourced pattern */}
          {(sourceContact || opp.sourceReferredBy) && (
            <div className="opp-source">
              <GitBranch size={18} className="opp-source-icon" />
              <div>
                <div className="opp-source-label">Advisor-Sourced</div>
                <div className="opp-source-body">
                  Introduced by{' '}
                  {sourceContact
                    ? <Link to={`/contacts/${sourceContact.id}`}>{contactName(sourceContact)}</Link>
                    : <b>{opp.sourceReferredBy}</b>}
                  {sourceAccount && <> at <Link to={`/accounts/${sourceAccount.id}`}>{sourceAccount.name}</Link></>}
                  {(opp.type === 'Equity Partner' || opp.type === 'JV') && <>{' '}— landing as a direct WPI investor relationship.</>}
                </div>
              </div>
            </div>
          )}

          <SectionCard title={<>Key <b>Information</b></>} eyebrow="Deal record">
            {opp.description && <p className="opp-narrative">{opp.description}</p>}
            <div style={{ marginTop: opp.description ? 'var(--sp-4)' : 0 }}>
              <DescriptionList rows={commonRows} columns={2} />
            </div>
          </SectionCard>

          {/* ---- type-specific: Equity Partner ---- */}
          {opp.type === 'Equity Partner' && (
            <SectionCard title={<>Capital <b>Commitment</b></>} eyebrow="Equity Partner">
              <EquityCommitment opp={opp} fundCommitted={fund?.committedCapital} fundName={fund?.name} />
            </SectionCard>
          )}

          {/* ---- type-specific: 3PM ---- */}
          {opp.type === '3PM' && (
            <SectionCard title={<>Competitive <b>Standing</b></>} eyebrow="Third-party management">
              <DescriptionList
                columns={2}
                rows={[
                  {
                    label: 'Competitive Position',
                    value: opp.competitivePosition
                      ? <Badge tone={POSITION_TONE[opp.competitivePosition]} variant="soft">{opp.competitivePosition}</Badge>
                      : '—',
                  },
                  { label: 'Fee Terms', value: opp.feeTerms ?? '—' },
                  { label: 'Est. Onboard', value: opp.estOnboardDate ? date(opp.estOnboardDate, 'long') : '—' },
                  { label: 'Sites Managed', value: linkedProps.length || '—' },
                ]}
              />
              {opp.topCompetitors && opp.topCompetitors.length > 0 && (
                <div style={{ marginTop: 'var(--sp-4)' }}>
                  <div className="label" style={{ marginBottom: 'var(--sp-2)' }}>Top Competitors</div>
                  <div className="opp-competitors">
                    {opp.topCompetitors.map((c) => (
                      <span key={c} className="opp-competitor"><Swords size={12} />{c}</span>
                    ))}
                  </div>
                </div>
              )}
            </SectionCard>
          )}

          {/* ---- type-specific: Acquisition ---- */}
          {opp.type === 'Acquisition' && <AcquisitionBlock opp={opp} />}

          {/* ---- type-specific: Development ---- */}
          {opp.type === 'Development' && <DevelopmentBlock opp={opp} />}

          {/* ---- deal notes ---- */}
          {opp.dealNotes && (
            <SectionCard title={<>Deal <b>Notes</b></>} eyebrow="Working narrative">
              <p className="opp-narrative">{opp.dealNotes}</p>
            </SectionCard>
          )}

          {/* ---- linked properties ---- */}
          {linkedProps.length > 0 && (
            <SectionCard
              title={<>Linked <b>Properties</b></>}
              eyebrow="Portfolio"
              right={<span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{linkedProps.length} sites</span>}
            >
              <div className="opp-props">
                {linkedProps.map((p) => {
                  const occ = latest(p.occupancyPct)
                  return (
                    <div key={p.id} className="opp-prop">
                      <Link to={`/properties/${p.id}`} className="opp-prop-name">{p.name}</Link>
                      <span className="opp-prop-meta">
                        {p.address?.city ? `${p.address.city}, ${p.address.state ?? ''}` : p.relationshipType}
                      </span>
                      <span className="opp-prop-meta">
                        Occ <span className="opp-prop-occ">{occ != null ? `${occ.toFixed(1)}%` : '—'}</span>
                      </span>
                    </div>
                  )
                })}
              </div>
            </SectionCard>
          )}

          {/* ---- activity timeline ---- */}
          <SectionCard title={<>Deal <b>Activity</b></>} eyebrow="History">
            {activities.length > 0
              ? <Timeline activities={activities} />
              : <EmptyState icon={<FileText size={28} />} title="No activity logged yet" hint="Calls, emails, and meetings on this deal will appear here." />}
          </SectionCard>
        </div>

        {/* ---------- RAIL ---------- */}
        <div className="opp-rail">
          {principal && (
            <AssociationCard title="Principal Account" icon={<Building2 size={14} />}>
              <AssocRow
                primary={<Link to={`/accounts/${principal.id}`}>{principal.name}</Link>}
                secondary={`${principal.type} · ${principal.status}`}
              />
            </AssociationCard>
          )}

          {fund && (
            <AssociationCard title="Fund / Vehicle" icon={<Landmark size={14} />}>
              <AssocRow
                primary={<Link to={`/funds/${fund.id}`}>{fund.name}</Link>}
                secondary={fund.strategy}
                meta={fund.vintage ? <span className="muted">{fund.vintage}</span> : undefined}
              />
            </AssociationCard>
          )}

          <AssociationCard title="Key Dates" count={keyDates.length} icon={<CalendarClock size={14} />}>
            {keyDates.length > 0
              ? keyDates.map((k) => {
                  const d = daysUntil(k.date)
                  return (
                    <AssocRow
                      key={k.id}
                      primary={k.title}
                      secondary={`${k.type} · ${date(k.date, 'med')}`}
                      meta={
                        <Badge tone={KD_TONE[k.status]} variant="soft">
                          {d != null && d >= 0 ? `${d}d` : k.status}
                        </Badge>
                      }
                    />
                  )
                })
              : <div className="dim" style={{ padding: 'var(--sp-2) 0', fontSize: 'var(--fs-sm)' }}>No key dates tracked.</div>}
          </AssociationCard>

          {opp.contactRoles && opp.contactRoles.length > 0 && (
            <AssociationCard title="Deal Contacts" count={opp.contactRoles.length}>
              {opp.contactRoles.map((cr) => {
                const c = contactById(data, cr.contactId)
                if (!c) return null
                return (
                  <AssocRow
                    key={cr.contactId}
                    primary={<Link to={`/contacts/${c.id}`}>{contactName(c)}</Link>}
                    secondary={c.title}
                    meta={<Badge tone={cr.side === 'WPI' ? 'navy' : 'neutral'} variant="outline">{cr.role}</Badge>}
                  />
                )
              })}
            </AssociationCard>
          )}
        </div>
      </div>
    </>
  )
}

/* ---------- Equity Partner: commitment vs fund ---------- */
function EquityCommitment({ opp, fundCommitted, fundName }: { opp: Opportunity; fundCommitted?: number; fundName?: string }) {
  const ticket = opp.dealSize ?? 0
  // Show this LP's prospective ticket against the existing committed base if available.
  const base = fundCommitted ?? 0
  const total = base + ticket
  const pct = total > 0 ? (ticket / total) * 100 : 0
  return (
    <div className="opp-commit">
      <DescriptionList
        columns={2}
        rows={[
          { label: 'Prospective Ticket', value: ticket ? money(ticket) : '—' },
          { label: 'Target Vehicle', value: fundName ?? 'WPI Self-Storage Equity' },
          { label: 'Est. Close', value: opp.estCloseQuarter ?? '—' },
          { label: 'Capital Profile', value: opp.description?.includes('long-hold') || opp.description?.includes('Long-hold') ? 'Long-hold / patient capital' : 'Institutional LP' },
        ]}
      />
      {base > 0 && (
        <>
          <div className="opp-commit-bar"><div className="opp-commit-fill" style={{ width: `${pct}%` }} /></div>
          <div className="opp-commit-legend">
            <span>This LP ticket <b>{money(ticket, { compact: true })}</b></span>
            <span>Existing committed base <b>{money(base, { compact: true })}</b></span>
          </div>
        </>
      )}
    </div>
  )
}

/* ---------- Acquisition block (only renders fields that exist) ---------- */
function AcquisitionBlock({ opp }: { opp: Opportunity }) {
  const rows: DLRow[] = [
    { label: 'Status', value: opp.acquisitionStatus ?? '—' },
    { label: 'Seller', value: opp.seller ?? '—' },
    { label: 'Buyer', value: opp.buyer ?? '—' },
    { label: 'Broker', value: opp.broker ?? '—' },
    { label: 'Initial Bid', value: opp.initialBid != null ? money(opp.initialBid) : '—' },
    { label: 'Current Bid', value: opp.currentBid != null ? money(opp.currentBid) : '—' },
    { label: 'Strategy', value: opp.strategy ?? '—' },
    { label: 'Criteria', value: opp.criteria ?? '—' },
    { label: 'DD Expiration', value: opp.ddExpiration ? date(opp.ddExpiration, 'long') : '—' },
    { label: 'Hard Money', value: opp.hardMoneyDate ? date(opp.hardMoneyDate, 'long') : '—' },
  ].filter((r) => r.value !== '—')
  return (
    <SectionCard title={<>Acquisition <b>Terms</b></>} eyebrow="Deal mechanics">
      {rows.length > 0
        ? <DescriptionList rows={rows} columns={2} />
        : <EmptyState title="No acquisition terms recorded" hint="Bid, status, and diligence dates will appear once underwriting begins." />}
    </SectionCard>
  )
}

/* ---------- Development block (only renders fields that exist) ---------- */
function DevelopmentBlock({ opp }: { opp: Opportunity }) {
  const rows: DLRow[] = [
    { label: 'Entitlement Status', value: opp.entitlementStatus ?? '—' },
    { label: 'Permitting Obstacles', value: opp.permittingObstacles ?? '—' },
    { label: 'Required Studies', value: opp.requiredStudies?.join(', ') ?? '—' },
    { label: 'Hearing Dates', value: opp.hearingDates?.map((d) => date(d, 'med')).join(' · ') ?? '—' },
  ].filter((r) => r.value !== '—')
  return (
    <SectionCard title={<>Entitlement <b>Track</b></>} eyebrow="Development">
      {rows.length > 0
        ? <DescriptionList rows={rows} columns={2} />
        : <EmptyState title="No entitlement detail recorded" hint="Hearing dates, studies, and obstacles will appear as the project advances." />}
      {opp.outreachNotes && <p className="opp-narrative" style={{ marginTop: 'var(--sp-3)' }}>{opp.outreachNotes}</p>}
    </SectionCard>
  )
}
